import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import './styles/proDetail.css';
import type useOrderEditing from '../../hooks/useOrderEditing';
import type { OrderDetails } from '../../services/orders.service';
import PageHeader from './components/PageHeader';
import Tabs, { PRO_TABS, PRO_TAB_IDS, PRO_EDITABLE_TABS, type TabDef } from './components/Tabs';
import { useModeTerminalSaveGuard } from './useModeTerminalSaveGuard';
import OrderFormView from './components/OrderFormView';
import AddlInfoView from './components/AddlInfoView';
import TracingView from './components/TracingView';
import TraceButton from '../../components/trace/TraceButton';
import { type TraceCarrierType, type TraceResult } from '../../services/tracking.service';
import { unstageTracedEdits } from './traceOverlay';
import AddlCostView from './components/AddlCostView';
import InvoicingView from './components/InvoicingView';
import LegsGrid, { type DriverValidationSnapshot } from './components/LegsGrid';
import PermissionsContainer from '../../components/PermissionsContainer';
import { TEMPLATE_EDIT_FIELDS_COMPONENT_ID } from './useCanEditTemplate';
import type { LegEta } from '../../components/driver-status-monitor/etaProjection';
import type { DriverPayLeg } from '../../services/proDocuments.service';
import { isTemplateApproved } from './templateFieldPolicy';
import { outsideCarrierDriverFor } from '../../utils/outsideCarrier';
import { useOrderFieldDisplay } from '../../hooks/useOrderFieldDisplay';
import { OrderFieldDisplayProvider } from './OrderFieldDisplayContext';
import { useAdminOverride } from './AdminOverrideContext';
import { AdminOverrideButton, AdminOverrideBanner } from './AdminOverrideControls';
import type { OverrideApprovalRecord } from '../../services/dispatchOverrideApproval.service';
import NotesTab from './tabs/NotesTab';
import HistoryTab from './tabs/HistoryTab';
import DocsTab from './tabs/DocsTab';
import StagedDocsTab from './tabs/StagedDocsTab';
import type { StagedDoc } from './createOrder/stagedDocs';
import WhseView from './tabs/WhseView';
import ChangeAllBanner from './changeAll/ChangeAllBanner';
import FollowUpBookmark from '../../components/FollowUpBookmark';
import { proFollowUp } from '../../utils/followUpLabels';

type Editing = ReturnType<typeof useOrderEditing>;

type Props = {
  /** Numeric order pronumber, or a "T"-prefixed template pronumber ("T110"). */
  pronumber: number | string;
  /** Read-only Template view: limits the visible tabs and shows a badge. */
  isTemplate?: boolean;
  /** CSR/LC template-edit gate (from useCanEditTemplateFields). Only meaningful
   *  for templates; threaded into each view's template canEdit branch so a
   *  non-LC user sees fields but can't edit them. Defaults to editable. */
  canEditTemplate?: boolean;
  orderDetails: OrderDetails;
  editing: Editing;
  activeTab: string;
  onTabChange: (id: string) => void;
  /** Addl Info "Deleted" field -> Y: run the eligibility check + reason modal.
   *  Owned by ProDetailContainer (does the immediate delete save). */
  onDeleteAttempt?: () => void;
  /** True while the delete-eligibility check is running — locks the Deleted field
   *  and shows a checking indicator. */
  deleteChecking?: boolean;
  /** Rolling Fault set while Booking Hold is ROLLING: open the "Reason for
   *  Rolling" prompt. Owned by ProDetailContainer (captures the values, then
   *  saves the record). */
  onRollingFaultSet?: (fault: string) => void;
  /** Per Diem Responsibility note just saved by the Addl Cost prompt — the host
   *  stages it so a Change All fan-out carries the same answer to every pro. */
  onPerDiemNoteSaved?: (note: string) => void;
  /** Reports the live trace overlay — the order-model values a click-trace just
   *  wrote (keyed by OrderDetails key), which this view layers over `orderDetails`
   *  on screen. The host needs the same layer wherever it treats `orderDetails` as
   *  the anchor's CURRENT values: a Change All fan-out of a flagged field the user
   *  never typed in must copy what the trace filled (1st Rcv / Doc Cut / Cutoff),
   *  not the blank from the pre-trace fetch. Called with {} when the overlay
   *  clears (PRO change). */
  onTracedOverlayChange?: (overlay: Record<string, unknown>) => void;
  position?: { current: number; total: number };
  onPrev?: () => void;
  onNext?: () => void;
  /** Drawer mode: skip the Pro#/badges title row in PageHeader. */
  hideTitleRow?: boolean;
  /** Forwarded into LegsGrid so ProDetailContainer can read the latest
   *  per-leg driver validation snapshot at save time (override-approval flow). */
  driverValidationSnapshotRef?: React.RefObject<DriverValidationSnapshot>;
  /** Recorded driver-qualification overrides for this PRO, used to drive the
   *  inline indicator on legs whose current driver matches an approval. */
  overrideApprovals?: OverrideApprovalRecord[];
  /** Bumped after an auto-triggered REJECTED trouble ticket is saved so the
   *  OrderFormView Trouble Tickets sub-tab badge re-fetches its count. */
  troubleTicketRefreshNonce?: number;
  /** Per-leg driver-ETA projection (keyed by legnum), fed to LegsGrid's ETA
   *  column. Undefined/empty renders the column blank. */
  etaByLeg?: Map<number, LegEta>;
  /** Per-leg driver pay + margin (keyed by legnum) from the showmargin
   *  read-model, fed to LegsGrid's Pay/Margin cells. Blank when absent. */
  marginByLeg?: Map<number, DriverPayLeg>;
  /** Pro's margin limit (%), used to color per-leg margin red when under it. */
  marginLimit?: number;
  /** True while the per-Pro margin is still loading (fetched after open). */
  marginLoading?: boolean;
  /** Create New Order mode: the record doesn't exist yet, so tabs that can only
   *  read persisted rows are hidden and the docs/notes tabs stage locally. */
  createMode?: boolean;
  /** Create-mode staging surfaces, owned by the create flow (they live outside
   *  the editing hook's model so they survive tab unmounts). */
  createStaging?: {
    stagedDocs: StagedDoc[];
    onStagedDocsChange: (next: StagedDoc[]) => void;
    stagedNotes: string[];
    onStagedNotesChange: (next: string[]) => void;
    /** 'T###' the order is being created from, when any. */
    templatePro?: string;
  };
};

// Tabs with nothing to show before the order exists: whse + history rows can't
// exist pre-save, and Addl Cost derives entirely from persisted rows (V1 also
// excluded the accessorial actions for a new order).
const CREATE_HIDDEN_TABS = new Set(['whse', 'addl-cost', 'history']);

// Re-exported for back-compat with anything that still imports these from
// ProDetail. New consumers should import from `./components/Tabs`.
export { PRO_TABS, PRO_TAB_IDS, PRO_EDITABLE_TABS };

const ProDetail: React.FC<Props> = ({
  pronumber,
  isTemplate = false,
  canEditTemplate = true,
  orderDetails,
  editing,
  activeTab,
  onTabChange,
  onDeleteAttempt,
  deleteChecking,
  onRollingFaultSet,
  onPerDiemNoteSaved,
  onTracedOverlayChange,
  position,
  onPrev,
  onNext,
  hideTitleRow,
  driverValidationSnapshotRef,
  overrideApprovals,
  troubleTicketRefreshNonce,
  etaByLeg,
  marginByLeg,
  marginLimit,
  marginLoading,
  createMode = false,
  createStaging,
}) => {
  // Mode/Terminal hard block. Owned here, not by the Order Info tab: a save-blocking
  // guard has to hold whatever tab is open, and the tabs unmount (bugfix #33440).
  useModeTerminalSaveGuard(orderDetails, editing?.editedOrderFields, editing?.setExtraSaveErrors);

  // LegsGrid keys write affordances off a numeric pronumber. Templates are
  // read-only so this only ever drives display for them; pass the numeric part
  // (for a real order this equals `pronumber`, so the order path is unchanged).
  const numericPro =
    typeof pronumber === 'number'
      ? pronumber
      : parseInt(String(pronumber).replace(/\D/g, ''), 10) || 0;

  // Trace overlay: the freshly-traced order values + which keys changed, held
  // in local state so a trace updates the on-screen fields in place (with
  // highlighting) — no re-fetch, so the user's unsaved edits are never dropped.
  // Cleared when navigating to a different PRO.
  const [tracedOverlay, setTracedOverlay] = useState<Record<string, unknown>>({});
  const [tracedKeys, setTracedKeys] = useState<string[]>([]);
  useEffect(() => {
    setTracedOverlay({});
    setTracedKeys([]);
  }, [pronumber]);
  // The live editing hook for the trace handler below, which is memoised once:
  // the hook's identity changes on every edit, a ref does not.
  const editingRef = useRef(editing);
  editingRef.current = editing;
  const proRef = useRef(numericPro);
  proRef.current = numericPro;
  const handleTraced = useCallback((r: TraceResult) => {
    // A late answer belongs to the PRO it was asked for. The popup now opens
    // at once and the port answers seconds later; a fast LC can be on the
    // next PRO by then, and this screen's overlay was reset for it above.
    if (r.pronumber !== proRef.current) return;
    // The overlay carries CMS's current value of every field the trace
    // evaluated, changed or not, so a field blanked on screen but not saved
    // comes back on the click (RICGCU258500, 2026-10-14); `updated_keys` is
    // the changed subset and drives the highlight only.
    const overlay = r.updated_order_fields ?? {};
    if (!Object.keys(overlay).length) return;
    setTracedOverlay((prev) => ({ ...prev, ...overlay }));
    if (r.updated_keys?.length) {
      setTracedKeys((prev) => Array.from(new Set([...prev, ...(r.updated_keys ?? [])])));
    }
    // The form shows edited > traced > fetched, and a cleared picker stages
    // '' as an edit, so the overlay alone never reached a blanked field: the
    // popup showed the dates and the inputs stayed empty. Un-stage the edits
    // the traced value should now show through (see traceOverlay.ts for the
    // rule: changed columns always, unchanged ones only over a blank).
    unstageTracedEdits(r, editingRef.current);
  }, []);
  // Mirror the overlay to the host so its idea of the anchor's current values
  // matches what this screen shows (see the prop doc).
  useEffect(() => {
    onTracedOverlayChange?.(tracedOverlay);
  }, [tracedOverlay, onTracedOverlayChange]);

  // Template approval gate (order-level `approvedby`, surfaced as
  // `invoiceApprovedBy`). Only meaningful for templates; drives which State-2
  // leg fields lock. Always false for a normal order.
  const isApproved = isTemplate && isTemplateApproved(orderDetails);

  // Outside-carrier pseudo-driver (ORDERS ONLY): when the (effective) dispatcher
  // is an outside carrier AND a carrier is chosen, the driver field gains a
  // selectable pseudo-unit = the carrier's SCAC (or the first 6 alphanumeric
  // chars of its name). Computed from the edited-or-saved values so it tracks
  // unsaved dispatcher/carrier edits, then flowed into every leg's driver cell
  // via LegsGrid. Empty string when it doesn't apply (LegsGrid ignores it).
  //
  // Templates NEVER open the driver field — the leg grid stays read-only there —
  // so force it empty for a template. That keeps the driver cell fully locked on
  // templates and removes any chance of the numeric pronumber (parseInt of
  // "T110" → 110) reaching a driver-qualification call.
  const edited = (editing?.editedOrderFields ?? {}) as Record<string, unknown>;
  const savedFields = orderDetails as unknown as Record<string, unknown>;
  const eff = (key: keyof OrderDetails & string): string => {
    const raw = edited[key] ?? savedFields[key];
    return raw == null ? '' : String(raw);
  };
  const outsideCarrierDriver = isTemplate
    ? ''
    : outsideCarrierDriverFor(eff('dispatcher'), eff('carrierScac'), eff('carrier'));

  // Config-driven DISPLAY layer (order_field_display_rules). Here it drives
  // per-movetype TAB hiding (e.g. Whse on DOMESTIC / REPO / RAIL TO PORT); the
  // order views use the same hook for per-field hide/readonly. Keyed on the
  // EFFECTIVE movetype so an unsaved movetype edit re-hides/re-shows live.
  // Orders only (system 'cms') — templates show every tab until the template
  // ruleset is seeded (Phase 5), so tab-hiding is gated on !isTemplate below.
  // Is this order invoiced? Same rule as /pro's own reader and as the grid's
  // order_states: the MAIN invoice (accessorialnum = 0, not soft-deleted) carrying a real
  // invoicedate. `invoiceMain` is already null unless that row exists, so its presence
  // plus a date is the whole test. This is what activates the INVOICED display scope —
  // until now it was never passed, so the seeded INVOICED rules did nothing on the form.
  const isInvoiced = !isTemplate
    && String(orderDetails.invoiceMain?.invoiceDate ?? '').trim() !== '';

  // Admin Override (#33501). OWNED by ProDetailContainer — useOrderEditing lives up
  // there and its save payloads have to carry the flag — so this only consumes it.
  const adminOverride = useAdminOverride();

  const fieldDisplay = useOrderFieldDisplay(
    eff('moveType'), isInvoiced, { overrideActive: adminOverride.active },
  );

  // Templates now show every order tab. Data-backed tabs (notes/history/docs)
  // fetch the template's own rows via template-aware endpoints; order-only
  // tabs (addl-cost/invoicing/reschedules/etc.) return empty gracefully.
  const stagedDocCount = createStaging?.stagedDocs.length ?? 0;
  const stagedNoteCount = createStaging?.stagedNotes.length ?? 0;
  // Follow Up bookmark, anchored right of the tab strip. ONE placement covers
  // both page and drawer mode: ProDetail renders the strip in both (drawer mode
  // only hides the PageHeader title row), which is why this replaced the
  // separate toggles that used to sit in PageHeader and the slide-over chrome.
  // proFollowUp returns null on a create-mode "NEW" pronumber, so no bookmark
  // is offered before the order has an identity to point at.
  const followUpBookmark = useMemo(() => {
    const bookmark = proFollowUp(pronumber, orderDetails.customerName);
    return bookmark ? (
      <FollowUpBookmark type={bookmark.type} itemId={bookmark.itemId} label={bookmark.label} />
    ) : null;
  }, [pronumber, orderDetails.customerName]);

  // The tab strip renders in BOTH page and drawer mode (drawer mode only drops the
  // PageHeader title row), so the override toggle rides here rather than in PageHeader —
  // the drawer is a real pro view and needs the same capability. Renders nothing for a
  // caller without the grant.
  const tabRightSlot = useMemo(() => (
    <span className="flex items-center gap-2">
      <AdminOverrideButton />
      {followUpBookmark}
    </span>
  ), [followUpBookmark]);

  const tabs = useMemo<ReadonlyArray<TabDef>>(
    () =>
      PRO_TABS.filter(
        (t) =>
          !(createMode && CREATE_HIDDEN_TABS.has(t.id)) &&
          (isTemplate || !fieldDisplay.isTabHidden(t.id)),
      ).map((t) => {
        // In create mode the badges count what's staged, not what's persisted.
        if (t.id === 'notes') {
          const count = createMode ? stagedNoteCount : orderDetails.noteCount;
          return count ? { ...t, badge: count } : t;
        }
        if (t.id === 'docs') {
          const count = createMode ? stagedDocCount : orderDetails.docCount;
          return count ? { ...t, badge: count } : t;
        }
        return t;
      }),
    [
      orderDetails.noteCount,
      orderDetails.docCount,
      isTemplate,
      fieldDisplay,
      createMode,
      stagedDocCount,
      stagedNoteCount,
    ],
  );

  const renderBody = () => {
    // Per-field trace controls (the v1 ➡️). Only real, non-template orders can
    // be traced. Terminal (Order Info) forces the rail side; the Tracing tab's
    // Trace Container derives its side from the selected unit (render-prop).
    const tracePro = !isTemplate && typeof pronumber === 'number' ? pronumber : null;
    // Pass the CURRENTLY-SELECTED (possibly unsaved) Terminal/SSLine so the
    // trace honors the on-screen dropdown, not the persisted order value.
    const terminalTraceControl =
      tracePro != null ? (
        <TraceButton pronumber={tracePro} variant="arrow" tracetype="rail" carrier={eff('rail')} onTraced={handleTraced} />
      ) : undefined;
    const scacTraceControl =
      tracePro != null ? (
        <TraceButton pronumber={tracePro} variant="arrow" tracetype="scac" carrier={eff('scac')} onTraced={handleTraced} />
      ) : undefined;
    const traceRun =
      tracePro != null
        ? (tt?: TraceCarrierType, carrier?: string) => (
            <TraceButton pronumber={tracePro} variant="arrow" tracetype={tt} carrier={carrier} onTraced={handleTraced} />
          )
        : undefined;
    switch (activeTab) {
      case 'order-info':
        return (
          <div className="px-3 pt-3 pb-6 flex flex-col gap-3.5">
            <OrderFormView
              details={orderDetails}
              editing={editing}
              createMode={createMode}
              canEditTemplate={canEditTemplate}
              onProRefresh={editing.refresh}
              troubleTicketRefreshNonce={troubleTicketRefreshNonce}
              terminalTraceControl={terminalTraceControl}
              scacTraceControl={scacTraceControl}
              tracedOverlay={tracedOverlay}
              tracedKeys={tracedKeys}
              onRollingFaultSet={onRollingFaultSet}
            />
            <LegsGrid
              legs={editing?.effectiveLegs ?? orderDetails.legs ?? []}
              editing={editing}
              pronumber={numericPro}
              customerName={orderDetails.customerName ?? undefined}
              hazmat={orderDetails.hazmat ?? undefined}
              isDaycab={orderDetails.isDaycab ?? undefined}
              isTank={orderDetails.isTank ?? undefined}
              overweightPermitRequired={orderDetails.overweightPermitRequired ?? undefined}
              moveType={orderDetails.moveType ?? undefined}
              reuseContainerPro={orderDetails.reuseContainerPro ?? undefined}
              isDrop={orderDetails.isDrop ?? undefined}
              dropReleaseDatetime={orderDetails.dropReleaseDatetime ?? undefined}
              deliveryLeg={orderDetails.deliveryLeg ?? undefined}
              originateLeg={orderDetails.originateLeg ?? undefined}
              terminateLeg={orderDetails.terminateLeg ?? undefined}
              billTo={orderDetails.billTo ?? undefined}
              consigneeName={orderDetails.consigneeName ?? undefined}
              firstRcv={orderDetails.firstRcv ?? undefined}
              warnings={orderDetails.warnings}
              openTtCount={orderDetails.openTtCount}
              openTickets={orderDetails.openTickets}
              offhire={orderDetails.offhire}
              redeliveryExpirationDate={orderDetails.redeliveryExpirationDate}
              openRoCount={orderDetails.openRoCount}
              outsideCarrierDriver={outsideCarrierDriver}
              driverValidationSnapshotRef={driverValidationSnapshotRef}
              overrideApprovals={overrideApprovals}
              etaByLeg={etaByLeg}
              marginByLeg={marginByLeg}
              marginLimit={marginLimit}
              marginLoading={marginLoading}
              isTemplate={isTemplate}
              canEditTemplate={canEditTemplate}
              isApproved={isApproved}
            />
          </div>
        );
      case 'notes':
        // Create mode: notes are staged in the flow and ride the create payload.
        // The template's own notes are NOT shown — V1 doesn't copy them.
        return createMode && createStaging ? (
          <NotesTab
            pronumber={pronumber}
            stagedNotes={createStaging.stagedNotes}
            onStagedNotesChange={createStaging.onStagedNotesChange}
          />
        ) : (
          <NotesTab pronumber={pronumber} />
        );
      case 'history':
        return <HistoryTab pronumber={pronumber} />;
      case 'addl-info':
        return (
          <div className="px-3 pt-3 pb-6">
            <AddlInfoView
              details={orderDetails}
              editing={editing}
              canEditTemplate={canEditTemplate}
              createMode={createMode}
              onDeleteAttempt={onDeleteAttempt}
              deleteChecking={deleteChecking}
              onTemplateChanged={editing.refresh}
            />
          </div>
        );
      case 'tracing':
        return (
          <div className="px-3 pt-3 pb-6">
            <TracingView
              details={orderDetails}
              editing={editing}
              canEditTemplate={canEditTemplate}
              traceRun={traceRun}
              tracedOverlay={tracedOverlay}
              tracedKeys={tracedKeys}
            />
          </div>
        );
      case 'whse':
        return <WhseView pronumber={pronumber} />;
      case 'addl-cost':
        return (
          <div className="px-3 pt-3 pb-6">
            <AddlCostView
              details={orderDetails}
              pronumber={pronumber}
              onRefresh={editing.refresh}
              editing={editing}
              onPerDiemNoteSaved={onPerDiemNoteSaved}
            />
          </div>
        );
      case 'invoicing':
        return (
          <div className="px-3 pt-3 pb-6">
            <InvoicingView
              details={orderDetails}
              pronumber={pronumber}
              editing={editing}
              canEditTemplate={canEditTemplate}
              createMode={createMode}
              onSaved={editing.refresh}
            />
          </div>
        );
      case 'docs':
        // Create mode uses a SEPARATE tab: DocsTab's react-query mutations write
        // immediately against a persisted pro, which doesn't exist yet.
        return createMode && createStaging ? (
          <StagedDocsTab
            stagedDocs={createStaging.stagedDocs}
            onChange={createStaging.onStagedDocsChange}
            templatePro={createStaging.templatePro}
          />
        ) : (
          <DocsTab pronumber={pronumber} editing={editing} />
        );
      default:
        return (
          <div className="px-3 pt-3 pb-6 flex flex-col gap-3.5">
            <OrderFormView
              details={orderDetails}
              editing={editing}
              createMode={createMode}
              canEditTemplate={canEditTemplate}
              onProRefresh={editing.refresh}
              troubleTicketRefreshNonce={troubleTicketRefreshNonce}
              terminalTraceControl={terminalTraceControl}
              scacTraceControl={scacTraceControl}
              tracedOverlay={tracedOverlay}
              tracedKeys={tracedKeys}
              onRollingFaultSet={onRollingFaultSet}
            />
            <LegsGrid
              legs={editing?.effectiveLegs ?? orderDetails.legs ?? []}
              editing={editing}
              pronumber={numericPro}
              customerName={orderDetails.customerName ?? undefined}
              hazmat={orderDetails.hazmat ?? undefined}
              isDaycab={orderDetails.isDaycab ?? undefined}
              isTank={orderDetails.isTank ?? undefined}
              overweightPermitRequired={orderDetails.overweightPermitRequired ?? undefined}
              moveType={orderDetails.moveType ?? undefined}
              reuseContainerPro={orderDetails.reuseContainerPro ?? undefined}
              isDrop={orderDetails.isDrop ?? undefined}
              dropReleaseDatetime={orderDetails.dropReleaseDatetime ?? undefined}
              deliveryLeg={orderDetails.deliveryLeg ?? undefined}
              originateLeg={orderDetails.originateLeg ?? undefined}
              terminateLeg={orderDetails.terminateLeg ?? undefined}
              billTo={orderDetails.billTo ?? undefined}
              consigneeName={orderDetails.consigneeName ?? undefined}
              firstRcv={orderDetails.firstRcv ?? undefined}
              warnings={orderDetails.warnings}
              openTtCount={orderDetails.openTtCount}
              openTickets={orderDetails.openTickets}
              offhire={orderDetails.offhire}
              redeliveryExpirationDate={orderDetails.redeliveryExpirationDate}
              openRoCount={orderDetails.openRoCount}
              outsideCarrierDriver={outsideCarrierDriver}
              driverValidationSnapshotRef={driverValidationSnapshotRef}
              overrideApprovals={overrideApprovals}
              etaByLeg={etaByLeg}
              marginByLeg={marginByLeg}
              marginLimit={marginLimit}
              marginLoading={marginLoading}
              isTemplate={isTemplate}
              canEditTemplate={canEditTemplate}
              isApproved={isApproved}
            />
          </div>
        );
    }
  };

  return (
    <OrderFieldDisplayProvider value={fieldDisplay}>
    <div className="flex flex-col h-full bg-white dark:bg-gray-900">
      <AdminOverrideBanner />
      {!hideTitleRow && (
        <PageHeader
          pronumber={pronumber}
          isTemplate={isTemplate}
          deleted={orderDetails.addlInfoDeleted === 'Y'}
          movetype={orderDetails.moveType || undefined}
          classification={orderDetails.keyAccount || undefined}
          equipmentNumber={orderDetails.container || undefined}
          billTo={
            orderDetails.billToName
              ? { name: orderDetails.billToName, code: orderDetails.billTo }
              : null
          }
          customer={
            orderDetails.customerName
              ? { name: orderDetails.customerName, code: orderDetails.customer }
              : null
          }
          position={position}
          onPrev={onPrev}
          onNext={onNext}
        />
      )}
      <ChangeAllBanner editedOrderFields={editing?.editedOrderFields ?? {}} />
      {isTemplate ? (
        // Anchor the template edit-fields permissions cog on the tab bar — the
        // one element rendered in BOTH page and drawer modes (the drawer hides
        // the PageHeader). In permissions mode a developer sees the cog here to
        // grant CSR/LC groups edit access on the `template-edit-fields`
        // component; this also self-registers it. `defaultDisabled` keeps the
        // tabs visible for everyone. Only the tab bar sits under this container
        // (the body is a sibling), and Tabs isn't permission-aware, so this is a
        // pure config anchor — the useCanEditTemplateFields hook does the actual
        // per-field gating.
        <PermissionsContainer
          componentId={TEMPLATE_EDIT_FIELDS_COMPONENT_ID}
          componentName="Template Edit Fields"
          defaultDisabled
        >
          <Tabs tabs={tabs} activeId={activeTab} onChange={onTabChange} rightSlot={tabRightSlot} />
        </PermissionsContainer>
      ) : (
        <Tabs tabs={tabs} activeId={activeTab} onChange={onTabChange} rightSlot={tabRightSlot} />
      )}
      <div className="flex-1 min-h-0 overflow-auto">{renderBody()}</div>
    </div>
    </OrderFieldDisplayProvider>
  );
};

export default ProDetail;
